import {
  Avatar,
  Button,
  Divider,
  Stack,
  Tooltip,
  Typography,
  ClickAwayListener,
  Skeleton,
  useTheme,
  MenuItem,
  TextField,
  InputAdornment,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { makeStyles } from '@mui/styles';
import { enqueueSnackbar } from 'notistack';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuthContext } from 'src/auth/hooks';
import Iconify from 'src/components/iconify';
import Image from 'src/components/image';
import {
  editCandidateDetails,
  getPublicSharingLink,
  hireCandidate,
  rejectCandidate,
  shortlistCandidates,
} from 'src/services/candidate.service';
import { getOrganizationId } from 'src/services/user.service';
import SvgColor from 'src/components/svg-color';
import PropTypes from 'prop-types';
import CustomPopover, { usePopover } from 'src/components/custom-popover';
import { BASE_URL } from 'src/config-global';
import { useCopyToClipboard } from 'src/hooks/use-copy-to-clipboard';
import FlagMenu from 'src/components/flag-menu/flag-menu';
import { addTag, getTags } from 'src/services/role.service';
import Scrollbar from 'src/components/scrollbar';
import {
  CANDIDATE_BEST_MATCHING,
  CANDIDATE_CLOSE_TO_MATCH,
  CANDIDATE_MATCHING,
  ELIGIBLE_FOR_INTERVIEW,
  NOT_ELIGIBLE_FOR_INTERVIEW,
} from '../job-candidate/ai-recommendation-status-constants';
import {
  AI_INTERVIEW_CREATED,
  AI_INTERVIEW_EVALUATED,
  AI_INTERVIEW_IN_PROGRESS,
  AI_INTERVIEW_LINK_SENT,
  ANALYZING,
  CANDIDATE_HIRED,
  CANDIDATE_REJECTED,
  IN_PERSON_INTERVIEW_SCHEDULED,
  SCREENING_COMPLETED,
  SCREENING_DISQUALIFIED,
  SCREENING_ERROR,
  SCREENING_QUALIFIED,
  CODING_INTERVIEW_SUBMITTED,
} from '../job-candidate/job-candidate-status-constants';
import {
  AI_INTERVIEW,
  APPLIED,
  HIRED,
  INPERSON_INTERVIEW,
  NOT_SHORTLISTED,
  REJECTED,
  SHORTLISTED,
  SOURCED,
} from '../job-candidate/screening-status-constants';
import { InPersonState } from './constants/constants-nav';
import TrackingTimeline from './timeline';
import { formatFullDateString } from './utility';
import { CandidateProfileContext } from './context/candidate-profile-context';
import { useCandidateProfileContext } from './hooks/use-candidate-profile-context';

const useStyles = makeStyles(theme => ({
  mainDiv: {
    width: '30%',
    borderRight: `1px solid ${theme.palette.border.primaryBlack3}`,
    height: '100%',
    gap: '1.2rem',
  },
  component: {
    padding: '0rem 1rem',
  },
  airecommendationDiv: {
    flexDirection: 'row',
    padding: '0.5rem',
    background: theme.palette.background.primaryAccent2,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 7,
    gap: 5,
    position: 'absolute',
    right: '12px',
    top: '12px',
  },
  flagDiv: {
    padding: '0.25rem',
    background: theme.palette.background.primaryAccent2,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 7,
    gap: 5,
    position: 'absolute',
    left: '12px',
    top: '12px',
  },
  componentSocialMain: {
    width: '100%',
    gap: '0.8rem',
    paddingTop: '40px',
  },
  imgAndNameDiv: {
    gap: '1rem',
    alignItems: 'center',
    justifyContent: 'center',
  },
  avatar: {
    alignSelf: 'center',
    width: '4.5rem',
    height: '4.5rem',
    background: theme.palette.background.primaryYellow1,
  },
  nameAndCompanyDiv: {
    gap: '0.5rem',
    alignItems: 'center',
    justifyContent: 'center',
  },
  currentJob: {
    color: theme.palette.content.primaryBlack3,
    textAlign: 'center',
    margin: 0,
    lineHeight: 'normal',
  },
  userName: {
    textAlign: 'center',
  },
  socialIconsDiv: {
    gap: '0.75rem',
    justifyContent: 'center',
    [theme.breakpoints.down('md')]: {
      gap: '0.3rem',
    },
    [theme.breakpoints.down('sm')]: {
      gap: '0rem',
    },
  },
  iconPadDiv: {
    background: theme.palette.background.primaryGrey1,
    color: theme.palette.content.primaryBlack1,
    padding: 8,
    borderRadius: 8,
    [theme.breakpoints.down('md')]: {
      padding: 4,
    },
  },
  button: {
    width: '100%',
  },
  errButton: {
    width: '100%',
    background: theme.palette.content.primaryRed2,
    color: theme.palette.background.primaryRed1,
    '&:hover': {
      backgroundColor: theme.palette.background.primaryRed2,
    },
  },
  candidateDetailsDiv: {
    gap: '1rem',
  },
  candidateDetailsAndTrackingText: {
    fontWeight: 700,
    fontSize: 18,
    margin: 0,
  },
  detailsContentDiv: {
    gap: '1rem',
  },
  detailsContent: {
    justifyContent: 'space-between',
  },
}));

// if stage = NULL, then APPLIED
const JOB_CANDIDATE_STAGE_UI_MAPPING = {
  [SOURCED]: 'Applied',
  [APPLIED]: 'Applied',
  [SHORTLISTED]: 'Applied',
  [AI_INTERVIEW]: 'AI Interview',
  [NOT_SHORTLISTED]: 'Needs Review',
  [INPERSON_INTERVIEW]: 'Interview',
  // "OFFERED": "Offered",
  [HIRED]: 'Hired',
  [REJECTED]: 'Rejeced',
};

// if status = NULL, Candidate Added
const SCREENING_STATUS_UI_MAPPING = {
  [ANALYZING]: 'Candidate Added',
  [AI_INTERVIEW_CREATED]: 'AI Interview Created',
  [AI_INTERVIEW_LINK_SENT]: 'AI Interview Sent',
  [SCREENING_ERROR]: 'Uncategorized',
  [SCREENING_COMPLETED]: 'Screening Completed',
  [AI_INTERVIEW_IN_PROGRESS]: 'Interview in-progress',
  [AI_INTERVIEW_EVALUATED]: 'Interview Completed',
  [IN_PERSON_INTERVIEW_SCHEDULED]: 'Interview',
  [CODING_INTERVIEW_SUBMITTED]: 'Coding Interview Submitted',
  [CANDIDATE_REJECTED]: 'Rejected',
  [CANDIDATE_HIRED]: 'Hired',
  [SCREENING_QUALIFIED]: 'Qualified',
  [SCREENING_DISQUALIFIED]: 'Disqualified',
};

const AI_RECOMMENDATION_STATUS_UI_MAPPINGS = {
  [NOT_ELIGIBLE_FOR_INTERVIEW]: 'Disqualified',
  [ELIGIBLE_FOR_INTERVIEW]: 'Qualified',
  [CANDIDATE_MATCHING]: 'Qualified',
  [CANDIDATE_BEST_MATCHING]: 'Best Match',
  [CANDIDATE_CLOSE_TO_MATCH]: 'Qualified',
  [REJECTED]: 'Disqualified',
};

const rejectHandler = (orgId, jobId, userId, saveChanges) => {
  // console.log(orgId, jobId, userId)
  rejectCandidate(orgId, jobId, userId)
    .then(res => {
      console.log(res);
      saveChanges();
    })
    .catch(err => {
      console.log(err);
    });
};

const hireHandler = (orgId, jobId, userId, saveChanges) => {
  hireCandidate(orgId, jobId, userId)
    .then(res => {
      console.log(res);
      saveChanges();
    })
    .catch(err => {
      console.log(err);
    });
};

const shortListHandler = (jobId, userId, saveChanges) => {
  const payload = {
    user_list: [userId],
  };
  shortlistCandidates(jobId, payload)
    .then(res => {
      console.log(res);
      saveChanges();
    })
    .catch(err => {
      console.log(err);
    });
};

const useGetCta = (jobId, setNavGroup) => {
  const theme = useTheme();
  const orgId = getOrganizationId(useAuthContext()?.user);
  const candidateDetails = useContext(CandidateProfileContext)?.obj;
  const saveChanges = useContext(CandidateProfileContext)?.saveChanges;
  if (!candidateDetails) return null;
  let interviewUrl = null;
  if (candidateDetails?.interview_details?.interview_url) {
    interviewUrl =
      process.env.REACT_APP_INTERVIEW_CLIENT_URL +
      candidateDetails.interview_details.interview_url;
  }
  const userId = candidateDetails?.candidate_info?.user_id;

  const jobCandidateStage = candidateDetails?.job_candidate_stage;
  const jobCandidateStatus = candidateDetails?.screening_status;
  // const jobCandidateStage = AI_INTERVIEW;
  // const jobCandidateStatus = AI_INTERVIEW_EVALUATED;

  if (
    jobCandidateStage === AI_INTERVIEW &&
    [AI_INTERVIEW_CREATED, AI_INTERVIEW_LINK_SENT].includes(jobCandidateStatus)
  ) {
    // Copy AI Interview Link
    return [
      {
        content: 'Copy AI Interview Link',
        icon: <Iconify color="white" icon="tabler:link" />,
        func: () => {
          if (interviewUrl) {
            navigator.clipboard.writeText(interviewUrl).then(
              () => {
                enqueueSnackbar('URL copied to clipboard');
              },
              () => {
                enqueueSnackbar({
                  message: 'Failed to copy URL',
                  variant: 'error',
                });
              },
            );
          }
        },
      },
    ];
  }
  if (
    [SOURCED, APPLIED, NOT_SHORTLISTED].includes(jobCandidateStage) &&
    [SCREENING_COMPLETED, SCREENING_ERROR, SCREENING_DISQUALIFIED].includes(
      jobCandidateStatus,
    )
  ) {
    // Reject/Shortlist
    return [
      {
        content: 'Reject',
        icon: (
          <Iconify
            color={theme.palette.background.primaryRed1}
            icon="tabler:x"
          />
        ),
        func: () => rejectHandler(orgId, jobId, userId, saveChanges),
      },
      {
        content: 'Shortlist',
        icon: <SvgColor src="/assets/tick.svg" />,
        func: () => {
          shortListHandler(jobId, userId, saveChanges);
        }, // createInterview link shortlist api in listing
      },
    ];
  }
  if (
    jobCandidateStage === AI_INTERVIEW &&
    (jobCandidateStatus === AI_INTERVIEW_EVALUATED || jobCandidateStatus=== CODING_INTERVIEW_SUBMITTED)
  ) {
    // Reject/Schedule Interview
    return [
      {
        content: 'Reject',
        icon: (
          <Iconify
            color={theme.palette.background.primaryRed1}
            icon="tabler:x"
          />
        ),
        func: () => rejectHandler(orgId, jobId, userId, saveChanges),
      },
      {
        content: 'Schedule Interview',
        icon: <SvgColor src="/assets/users.svg" />,
        func: () => {
          setNavGroup(InPersonState);
        }, // tab change to inperson
      },
    ];
  }
  if (
    jobCandidateStage === INPERSON_INTERVIEW &&
    jobCandidateStatus === IN_PERSON_INTERVIEW_SCHEDULED
  ) {
    // Reject/Hire
    return [
      {
        content: 'Reject',
        icon: (
          <Iconify
            color={theme.palette.background.primaryRed1}
            icon="tabler:x"
          />
        ),
        func: () => rejectHandler(orgId, jobId, userId, saveChanges),
      },
      {
        content: 'Hire',
        icon: <SvgColor src="/assets/tick.svg" />,
        func: () => {
          hireHandler(orgId, jobId, userId, saveChanges);
        },
      },
    ];
  }
  if (jobCandidateStage === HIRED && jobCandidateStatus === CANDIDATE_HIRED) {
    // Hired (but not clickable)
    return [
      {
        content: 'Hired',
        icon: <SvgColor src="/assets/tick.svg" />,
        func: () => {},
      },
    ];
  }
  if (
    jobCandidateStage === REJECTED &&
    jobCandidateStatus === CANDIDATE_REJECTED
  ) {
    // Rejected
    return [
      {
        content: 'Rejected',
        icon: <Iconify color="white" icon="tabler:x" />,
        func: () => {},
      },
    ];
  }
  if ([SOURCED, APPLIED, NOT_SHORTLISTED].includes(jobCandidateStage)) {
    // Reject/Shortlist
    return [
      {
        content: 'Reject',
        icon: (
          <Iconify
            color={theme.palette.background.primaryRed1}
            icon="tabler:x"
          />
        ),
        func: () => rejectHandler(orgId, jobId, userId, saveChanges),
      },
      {
        content: 'Shortlist',
        icon: <Image src="/assets/tick.svg" />,
        func: () => {
          shortListHandler(jobId, userId, saveChanges);
        }, // createInterview link shortlist api in listing
      },
    ];
  }
};

const CandidateSocial = ({ jobId, setNavGroup, updateCandidateFlag }) => {
  // hooks
  const classes = useStyles();
  const shareProfilePopover = usePopover();
  const CTA = useGetCta(jobId, setNavGroup);
  const {
    obj: candidateDetails,
    candidateType,
    isLoading,
  } = useContext(CandidateProfileContext);
  const { copyToClipboard } = useCopyToClipboard();

  // states
  const [profileJson, setProfileJson] = useState(null);
  const [isInternalLinkCopied, setIsInternalLinkCopied] = useState(false);
  const [isPublicLinkCopied, setIsPublicLinkCopied] = useState(false);
  const [isGeneratingPublicLink, setIsGeneratingPublicLink] = useState(false);
  const [publicProfileLink, setPublicProfileLink] = useState('');
  const [openEmailTooltip, setOpenEmailTooltip] = useState(false);
  const [openPhoneTooltip, setOpenPhoneTooltip] = useState(false);

  // variables
  let currentCompany = null;
  const candidateInfo = candidateDetails?.candidate_info;
  let currentFlag =
    candidateDetails?.current_stage_flag === null
      ? null
      : candidateDetails?.current_stage_flag?.candidate_stage_flag_name;

  const setCurrentFlag = flag => {
    currentFlag = flag;
  };

  if (candidateDetails?.candidate_info?.current_role)
    currentCompany = candidateDetails?.candidate_info?.current_role;

  if (candidateDetails?.candidate_info?.current_org) {
    if (currentCompany)
      currentCompany += ` @${candidateDetails?.candidate_info?.current_org}`;
    else currentCompany = candidateDetails?.candidate_info?.current_org;
  }

  let AiRecommendation = null;

  if (candidateDetails?.ai_recommendation)
    AiRecommendation =
      AI_RECOMMENDATION_STATUS_UI_MAPPINGS[candidateDetails?.ai_recommendation];

  // functions
  const formatUrl = url => {
    if (!url) return null;
    if (url.startsWith('https://') || url.startsWith('http://')) {
      return url;
    }
    return `https://${url}`;
  };

  const handleGeneratePublicLink = async () => {
    try {
      setIsGeneratingPublicLink(true);

      const response = await getPublicSharingLink(
        jobId,
        candidateDetails?.candidate_info?.user_id,
      );

      if (response.success) {
        setPublicProfileLink(response.body.links.CANDIDATE_PUBLIC_PROFILE);
        enqueueSnackbar('Public link generated successfully');
      } else throw new Error(response.message);
    } catch (err) {
      console.log(err);
    } finally {
      setIsGeneratingPublicLink(false);
    }
  };

  const handleCopyPublicLink = () => {
    copyToClipboard({
      text: BASE_URL + publicProfileLink,
      onSuccess: () => {
        setIsPublicLinkCopied(true);
        setTimeout(() => {
          setIsPublicLinkCopied(false);
        }, 2000);
      },
    });
  };

  const handleCopyInternalLink = () => {
    const link = `${window.location.href}${
      window.location.href.includes('?') ? '&' : '?'
    }show-candidate-profile=${candidateDetails?.candidate_info?.user_id}`;

    copyToClipboard({
      text: link,
      onSuccess: () => {
        setIsInternalLinkCopied(true);
        setTimeout(() => {
          setIsInternalLinkCopied(false);
        }, 2000);
      },
    });
  };

  const updateCandFlag = flagName => {
    setCurrentFlag(flagName);
    const flag = {
      candidate_stage_flag_name: flagName,
    };
    const candPayload = {
      id: candidateDetails.candidate_info.user_id,
    };
    updateCandidateFlag(candPayload, flag);
  };

  // effects
  useEffect(() => {
    if (candidateDetails?.candidate_info?.profile_json) {
      try {
        const dumProfile = JSON.parse(
          candidateDetails?.candidate_info?.profile_json,
        );
        setProfileJson(dumProfile);
      } catch (error) {
        console.log('error occured');
      }
    }
    if (candidateDetails && candidateDetails?.public_profile_link) {
      setPublicProfileLink(candidateDetails?.public_profile_link);
    }
  }, [candidateDetails]);

  const user = {
    id: candidateInfo?.user_id,
    userImg: '',
    name: candidateInfo?.name,
    email: candidateInfo?.email,
    mobileNo: candidateInfo?.phone_number,
    linkedin: formatUrl(profileJson?.user_links?.linkedin),
    github: formatUrl(profileJson?.user_links?.github),
  };

  return (
    <>
      <Stack
        className={classes.component}
        direction="column"
        position="relative"
      >
        {isLoading ? (
          <Skeleton
            width={36}
            height={36}
            sx={{
              borderRadius: '6px',
              position: 'absolute',
              right: '12px',
              top: '12px',
            }}
          />
        ) : (
          candidateType === 'JOB_CANDIDATE' && (
            <>
              <Stack className={classes.flagDiv}>
                <FlagMenu
                  updateCandFlag={updateCandFlag}
                  currentFlag={currentFlag}
                  candidate_id={user?.id}
                />
              </Stack>
              <Stack className={classes.airecommendationDiv}>
                <SvgColor
                  style={{ marginTop: -4 }}
                  src="/assets/AI-recommendation.svg"
                />
                <Typography variant="input">{AiRecommendation}</Typography>
              </Stack>
            </>
          )
        )}
        <Stack className={classes.componentSocialMain}>
          <Stack className={classes.imgAndNameDiv}>
            {isLoading ? (
              <Skeleton variant="circular" width={72} height={72} />
            ) : (
              (user?.name || user?.email) && (
                <Avatar className={classes.avatar}>
                  {user?.name?.charAt(0).toUpperCase() ||
                    user?.email?.charAt(0).toUpperCase()}
                </Avatar>
              )
            )}
            <Stack className={classes.nameAndCompanyDiv}>
              {isLoading ? (
                <Skeleton width={110} height={10} />
              ) : (
                (user?.name || user?.email) && (
                  <Typography variant="h4" className={classes.userName}>
                    {user?.name || user?.email}
                  </Typography>
                )
              )}
              {isLoading ? (
                <Skeleton width={219} height={8} />
              ) : (
                currentCompany && (
                  <Typography
                    variant="paragraph"
                    className={classes.currentJob}
                  >
                    {currentCompany}
                  </Typography>
                )
              )}
            </Stack>
          </Stack>
          <Stack direction="row" className={classes.socialIconsDiv}>
            {isLoading ? (
              <Skeleton
                width={40}
                height={40}
                sx={{
                  borderRadius: '8px',
                }}
              />
            ) : (
              user?.email && (
                <ClickAwayListener
                  onClickAway={() => setOpenPhoneTooltip(false)}
                >
                  <div>
                    <Tooltip
                      title={
                        <Stack
                          direction="row"
                          alignItems="center"
                          spacing="8px"
                        >
                          <Typography variant="caption">
                            {user?.email}
                          </Typography>
                          <Iconify
                            sx={{ cursor: 'pointer' }}
                            icon="tabler:copy"
                            onClick={() => {
                              navigator.clipboard.writeText(user?.email).then(
                                () => {
                                  enqueueSnackbar('Email copied to clipboard');
                                },
                                () => {
                                  enqueueSnackbar({
                                    message: 'Failed to copy Email',
                                    variant: 'error',
                                  });
                                },
                              );
                            }}
                          />
                        </Stack>
                      }
                      open={openEmailTooltip}
                      onOpen={() => setOpenEmailTooltip(true)}
                      onClose={() => setOpenEmailTooltip(false)}
                    >
                      <Stack
                        onClick={() => setOpenEmailTooltip(true)}
                        className={classes.iconPadDiv}
                      >
                        <SvgColor src="/assets/email.svg" />
                      </Stack>
                    </Tooltip>
                  </div>
                </ClickAwayListener>
              )
            )}
            {isLoading ? (
              <Skeleton
                width={40}
                height={40}
                sx={{
                  borderRadius: '8px',
                }}
              />
            ) : (
              user?.mobileNo && (
                <ClickAwayListener
                  onClickAway={() => setOpenPhoneTooltip(false)}
                >
                  <div>
                    <Tooltip
                      title={
                        <Stack
                          direction="row"
                          alignItems="center"
                          spacing="8px"
                        >
                          <Typography variant="caption">
                            {user?.mobileNo}
                          </Typography>
                          <Iconify
                            sx={{ cursor: 'pointer' }}
                            icon="tabler:copy"
                            onClick={() => {
                              navigator.clipboard
                                .writeText(user?.mobileNo)
                                .then(
                                  () => {
                                    enqueueSnackbar(
                                      'Phone number copied to clipboard',
                                    );
                                  },
                                  () => {
                                    enqueueSnackbar({
                                      message: 'Failed to copy Phone number',
                                      variant: 'error',
                                    });
                                  },
                                );
                            }}
                          />
                        </Stack>
                      }
                      open={openPhoneTooltip}
                      onOpen={() => setOpenPhoneTooltip(true)}
                      onClose={() => setOpenPhoneTooltip(false)}
                    >
                      <Stack
                        onClick={() => setOpenPhoneTooltip(true)}
                        className={classes.iconPadDiv}
                      >
                        <SvgColor src="/assets/phone.svg" />
                      </Stack>
                    </Tooltip>
                  </div>
                </ClickAwayListener>
              )
            )}
            {isLoading ? (
              <Skeleton
                width={40}
                height={40}
                sx={{
                  borderRadius: '8px',
                }}
              />
            ) : (
              user?.linkedin && (
                <Link to={user?.linkedin} target="_blank">
                  <Stack className={classes.iconPadDiv}>
                    <SvgColor src="/assets/linkedin.svg" />
                  </Stack>
                </Link>
              )
            )}
            {isLoading ? (
              <Skeleton
                width={40}
                height={40}
                sx={{
                  borderRadius: '8px',
                }}
              />
            ) : (
              user?.github && (
                <Link to={user?.github} target="_blank">
                  <Stack className={classes.iconPadDiv}>
                    <SvgColor src="/assets/github.svg" />
                  </Stack>
                </Link>
              )
            )}
            {isLoading ? (
              <Skeleton
                width={40}
                height={40}
                sx={{
                  borderRadius: '8px',
                }}
              />
            ) : (
              candidateType === 'JOB_CANDIDATE' && (
                <>
                  <Stack
                    aria-haspopup="true"
                    aria-owns={
                      shareProfilePopover.open
                        ? 'share-profile-popover'
                        : undefined
                    }
                    sx={{
                      cursor: 'pointer',
                    }}
                    className={classes.iconPadDiv}
                    onClick={shareProfilePopover.onOpen}
                  >
                    <Iconify icon="tabler:share" />
                  </Stack>
                  <CustomPopover
                    id="share-profile-popover"
                    sx={{
                      bgcolor: 'background.primaryBlack1',
                      borderColor: 'border.primaryBlack1',
                      borderRadius: '12px',
                      marginTop: '-10px',
                    }}
                    open={shareProfilePopover.open}
                    onClose={shareProfilePopover.onClose}
                    anchorOrigin={{
                      vertical: 'top',
                      horizontal: 'center',
                    }}
                    transformOrigin={{
                      vertical: 'bottom',
                      horizontal: 'center',
                    }}
                    disableRestoreFocus
                  >
                    <Stack
                      direction="row"
                      px="8px"
                      alignItems="center"
                      justifyContent="center"
                      spacing="4px"
                    >
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => handleCopyInternalLink()}
                        sx={{
                          width: '180px',
                          bgcolor: isInternalLinkCopied
                            ? 'background.primaryGreen2'
                            : 'background.primaryWhite1',
                          color: isInternalLinkCopied
                            ? 'content.primaryGreen1'
                            : 'content.primaryBlack1',

                          '&:hover': {
                            color: isInternalLinkCopied
                              ? 'background.primaryGreen2'
                              : 'content.primaryBlack1',
                            bgcolor: isInternalLinkCopied
                              ? 'content.primaryGreen1'
                              : 'background.primaryGrey2',
                          },
                        }}
                        endIcon={
                          isInternalLinkCopied ? null : (
                            <Iconify icon="tabler:copy" />
                          )
                        }
                      >
                        <Typography variant="caption">
                          {isInternalLinkCopied ? 'Copied' : 'Internal Link'}
                        </Typography>
                      </Button>
                      <LoadingButton
                        size="small"
                        variant="outlined"
                        sx={{
                          width: '180px',
                          color: 'content.primaryWhite',
                          '&:hover': {
                            borderColor: 'border.primaryAccent1',
                            bgcolor: 'background.primaryAccent1',
                          },
                        }}
                        loading={isGeneratingPublicLink}
                        loadingPosition="start"
                        onClick={
                          publicProfileLink
                            ? () => handleCopyPublicLink()
                            : () => handleGeneratePublicLink()
                        }
                        endIcon={
                          publicProfileLink &&
                          !isPublicLinkCopied && <Iconify icon="tabler:copy" />
                        }
                      >
                        <Typography variant="caption">
                          {(() => {
                            if (publicProfileLink) {
                              return isPublicLinkCopied
                                ? 'Copied'
                                : 'Public Link';
                            }
                            return 'Generate Public Link';
                          })()}
                        </Typography>
                      </LoadingButton>
                    </Stack>
                  </CustomPopover>
                </>
              )
            )}
          </Stack>
          {isLoading
            ? null
            : candidateType === 'CANDIDATE' && (
                <Button
                  variant="contained"
                  onClick={() => handleCopyInternalLink()}
                  startIcon={<Iconify icon="tabler:share" />}
                >
                  Copy profile link
                </Button>
              )}
          <Stack>
            {isLoading ? (
              <Skeleton
                width="100%"
                height={48}
                sx={{
                  borderRadius: '12px',
                }}
              />
            ) : (
              CTA &&
              (CTA.length === 2 ? (
                <Stack direction="row" spacing="8px">
                  <Button
                    variant="contained"
                    size="large"
                    className={classes.errButton}
                    startIcon={CTA[0]?.icon}
                    onClick={() => {
                      CTA[0]?.func();
                    }}
                  >
                    {CTA[0]?.content}
                  </Button>
                  <Button
                    variant="contained"
                    size="large"
                    className={classes.button}
                    startIcon={CTA[1]?.icon}
                    onClick={() => {
                      CTA[1]?.func();
                    }}
                  >
                    {CTA[1]?.content}
                  </Button>
                </Stack>
              ) : (
                <Button
                  variant="contained"
                  size="large"
                  className={classes.button}
                  startIcon={CTA[0]?.icon}
                  onClick={() => {
                    CTA[0]?.func();
                  }}
                  disabled={['Hired', 'Rejected'].includes(CTA[0]?.content)}
                >
                  {CTA[0]?.content}
                </Button>
              ))
            )}
          </Stack>
        </Stack>
      </Stack>
      <Divider />
    </>
  );
};

const CandidateDetails = () => {
  const tagsPopover = usePopover();
  const classes = useStyles();
  const {
    obj: candidateDetails,
    candidateType,
    isLoading,
  } = useContext(CandidateProfileContext);
  const { user: userObj } = useAuthContext();
  const orgId = getOrganizationId(userObj);
  const [addNewTag, setAddNewTag] = useState(false);
  const [newTagInput, setNewTagInput] = useState('');

  const user = {
    ranking: candidateDetails?.interview_details?.rank,
    aiInterviewScore: candidateDetails?.interview_details?.total_score,
    stage: candidateDetails?.job_candidate_stage,
    status: candidateDetails?.screening_status,
    source: candidateDetails?.candidate_info?.candidate_source,
    tags: candidateDetails?.candidate_info?.tags,
    addedDate: candidateDetails?.created_at,
    lastAppliedJobTitle:
      candidateDetails?.candidate_info?.last_applied_job_title,
    lastAppliedJobStatus:
      candidateDetails?.candidate_info?.last_applied_job_status,
    candidateAddedDate: candidateDetails?.candidate_info?.created_at,
  };

  const [isFetchingTags, setIsFetchingTags] = useState(false);
  const [tags, setTags] = useState(user?.tags || []);
  const [allTags, setAllTags] = useState([]);
  const [sortedTags, setSortedTags] = useState([]);
  const [tagExists, setTagExists] = useState(true);

  // functions
  const sortTags = () => {
    const _sortedTags = [...allTags];

    // Move selected tags to the beginning
    const selectedTags = _sortedTags.filter(tag => tags.includes(tag));
    const unselectedTags = _sortedTags.filter(tag => !tags.includes(tag));

    // Sort selected tags alphabetically
    selectedTags.sort((a, b) => a.localeCompare(b));

    // Sort unselected tags alphabetically
    unselectedTags.sort((a, b) => a.localeCompare(b));

    // Combine the sorted arrays
    _sortedTags.length = 0;
    _sortedTags.push(...selectedTags);
    _sortedTags.push(...unselectedTags);

    // Update the state with the sorted tags
    setSortedTags(_sortedTags);
  };

  const fetchTags = useCallback(
    async (showLoader = true, shouldSort = false) => {
      try {
        if (showLoader) {
          setIsFetchingTags(true);
        }

        const response = await getTags(orgId);
        if (response.success) {
          setAllTags(response.body);

          if (sortedTags.length === 0) {
            const _sortedTags = [...response.body];

            // Move selected tags to the beginning
            const selectedTags = _sortedTags.filter(tag =>
              tags.some(t => t.toLowerCase() === tag.toLowerCase()),
            );
            const unselectedTags = _sortedTags.filter(
              tag => !tags.some(t => t.toLowerCase() === tag.toLowerCase()),
            );

            // Sort selected tags alphabetically
            selectedTags.sort((a, b) => a.localeCompare(b));

            // Sort unselected tags alphabetically
            unselectedTags.sort((a, b) => a.localeCompare(b));

            // Combine the sorted arrays
            _sortedTags.length = 0;
            _sortedTags.push(...selectedTags);
            _sortedTags.push(...unselectedTags);

            // Update the state with the sorted tags
            setSortedTags(_sortedTags);
          }

          return response.body;
        }
        throw new Error(response.message);
      } catch (err) {
        console.log('err', err);
      } finally {
        setIsFetchingTags(false);
      }
    },
    [orgId, sortedTags.length, tags],
  );

  const handleTagsPopover = () => {
    tagsPopover.onClose();
    setAddNewTag(false);
    setNewTagInput('');
    sortTags();
  };

  const handleAddNewTag = async () => {
    try {
      if (!newTagInput) {
        enqueueSnackbar('Tag name cannot be empty!', {
          variant: 'error',
        });
        return;
      }

      const payload = {
        candidate_tag: newTagInput,
      };

      const response = await addTag(orgId, payload);

      if (response.success) {
        await updateTags(newTagInput);
        const _allTags = await fetchTags();
        const _tags = [...tags, newTagInput];

        const _sortedTags = [..._allTags];

        // Move selected tags to the beginning
        const selectedTags = _sortedTags.filter(tag =>
          _tags.some(t => t.toLowerCase() === tag.toLowerCase()),
        );
        const unselectedTags = _sortedTags.filter(
          tag => !_tags.some(t => t.toLowerCase() === tag.toLowerCase()),
        );

        // Sort selected tags alphabetically
        selectedTags.sort((a, b) => a.localeCompare(b));

        // Sort unselected tags alphabetically
        unselectedTags.sort((a, b) => a.localeCompare(b));

        // Combine the sorted arrays
        _sortedTags.length = 0;
        _sortedTags.push(...selectedTags);
        _sortedTags.push(...unselectedTags);

        // Update the state with the sorted tags
        setSortedTags(_sortedTags);

        setAddNewTag(false);
        setNewTagInput('');
        enqueueSnackbar('Added new tag successfully');
      } else throw new Error(response.message);
    } catch (err) {
      console.log(err);
      enqueueSnackbar('Failed to add tag', {
        variant: 'error',
      });
    }
  };

  const updateTags = async tag => {
    try {
      const updatedTags = [...tags];
      const tagIndex = updatedTags.findIndex(
        t => t.toLowerCase() === tag.toLowerCase(),
      );
      if (tagIndex > -1) {
        updatedTags.splice(tagIndex, 1);
      } else {
        updatedTags.push(tag.charAt(0).toUpperCase() + tag.slice(1));
      }
      const payload = {
        tags: updatedTags,
      };

      const response = await editCandidateDetails(
        orgId,
        candidateDetails?.candidate_info?.user_id,
        payload,
      );

      if (response.success) {
        setTags(updatedTags);
      } else throw new Error(response.message);
    } catch (err) {
      console.log(err);
      enqueueSnackbar('Failed to add tag', {
        variant: 'error',
      });
    }
  };

  // effects
  useEffect(() => {
    if (!isLoading) {
      fetchTags();
    }
  }, [fetchTags, isLoading]);

  useEffect(() => {
    if (!isLoading && user?.tags) {
      setTags(user?.tags || []);
    }
  }, [user?.tags, isLoading]);

  useEffect(() => {
    const _tagExists = tags.some(
      tag => tag.toLowerCase() === newTagInput.toLowerCase(),
    );
    setTagExists(_tagExists);
  }, [newTagInput, tags]);

  return (
    <>
      <Stack className={classes.component}>
        <Stack className={classes.candidateDetailsDiv}>
          {isLoading ? (
            <Skeleton width={204} height={11} />
          ) : (
            <Typography
              variant="paragraph"
              className={classes.candidateDetailsAndTrackingText}
            >
              Candidate Details
            </Typography>
          )}
          <Stack className={classes.detailsContentDiv}>
            {isLoading ? (
              <>
                <Stack direction="row" className={classes.detailsContent}>
                  <Skeleton width={53} height={8} />
                  <Skeleton width={14} height={8} />
                </Stack>
                <Stack direction="row" className={classes.detailsContent}>
                  <Skeleton width={116} height={8} />
                  <Skeleton width={14} height={8} />
                </Stack>
                <Stack direction="row" className={classes.detailsContent}>
                  <Skeleton width={40} height={8} />
                  <Skeleton width={50} height={8} />
                </Stack>
                <Stack direction="row" className={classes.detailsContent}>
                  <Skeleton width={45} height={8} />
                  <Skeleton width={99} height={8} />
                </Stack>
                <Stack direction="row" className={classes.detailsContent}>
                  <Skeleton width={47} height={8} />
                  <Skeleton width={66} height={8} />
                </Stack>
                <Stack direction="row" className={classes.detailsContent}>
                  <Skeleton width={168} height={8} />
                  <Skeleton width={96} height={8} />
                </Stack>
              </>
            ) : (
              <>
                {candidateType === 'CANDIDATE' && (
                  <Stack direction="row" className={classes.detailsContent}>
                    <Typography variant="input">Job Applied</Typography>
                    <Tooltip title={user?.lastAppliedJobTitle || '-'}>
                      <Typography
                        variant="input"
                        sx={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          maxWidth: '130px',
                        }}
                      >
                        {user?.lastAppliedJobTitle || '-'}
                      </Typography>
                    </Tooltip>
                  </Stack>
                )}
                {candidateType === 'CANDIDATE' && (
                  <Stack direction="row" className={classes.detailsContent}>
                    <Typography variant="input">Last Status</Typography>
                    <Typography variant="input">
                      {user?.lastAppliedJobStatus
                        ? JOB_CANDIDATE_STAGE_UI_MAPPING[
                            user?.lastAppliedJobStatus
                          ]
                        : 'Candidate Added'}
                    </Typography>
                  </Stack>
                )}
                {candidateType === 'JOB_CANDIDATE' && (
                  <Stack direction="row" className={classes.detailsContent}>
                    <Typography variant="input">Ranking</Typography>
                    <Typography variant="input">
                      {user?.ranking ? user?.ranking : '-'}
                    </Typography>
                  </Stack>
                )}
                {candidateType === 'JOB_CANDIDATE' && (
                  <Stack direction="row" className={classes.detailsContent}>
                    <Typography variant="input">AI Interview Score</Typography>
                    <Typography variant="input">
                      {user?.aiInterviewScore !== null
                        ? user?.aiInterviewScore
                        : '-'}
                    </Typography>
                  </Stack>
                )}
                {user?.stage && (
                  <Stack direction="row" className={classes.detailsContent}>
                    <Typography variant="input">Stage</Typography>
                    <Typography variant="input">
                      {user?.stage
                        ? JOB_CANDIDATE_STAGE_UI_MAPPING[user?.stage]
                        : '-'}
                    </Typography>
                  </Stack>
                )}
                {candidateType === 'JOB_CANDIDATE' && (
                  <Stack direction="row" className={classes.detailsContent}>
                    <Typography variant="input">Status</Typography>
                    <Typography variant="input">
                      {user?.status
                        ? SCREENING_STATUS_UI_MAPPING[user?.status]
                        : 'Candidate Added'}
                    </Typography>
                  </Stack>
                )}
                {user?.source && (
                  <Stack direction="row" className={classes.detailsContent}>
                    <Typography variant="input">Source</Typography>
                    <Typography variant="input">
                      {user?.source ? user?.source : '-'}
                    </Typography>
                  </Stack>
                )}
                <Stack
                  direction="row"
                  alignItems="center"
                  className={classes.detailsContent}
                >
                  <Typography variant="input">Tags</Typography>
                  <Stack direction="row" alignItems="center" spacing="8px">
                    <Tooltip title="Add tag">
                      <Stack
                        alignItems="center"
                        justifyContent="center"
                        px="8px"
                        py="3px"
                        sx={{
                          background: 'background.primaryGrey1',
                          border: theme =>
                            `1px solid ${theme.palette.border.primaryBlack3}`,
                          '&:hover': {
                            color: 'content.accent1',
                            background: 'background.primaryAccent2',
                            border: theme =>
                              `1px solid ${theme.palette.border.primaryAccent1}`,
                            borderRadius: '4px',
                            cursor: 'pointer',
                          },
                        }}
                        onClick={tagsPopover.onOpen}
                      >
                        <Typography variant="caption">+</Typography>
                      </Stack>
                    </Tooltip>
                    {tags.slice(0, 1).map(tag => (
                      <Stack
                        key={tag}
                        alignItems="center"
                        justifyContent="center"
                        px="8px"
                        py="3px"
                        sx={{
                          background: 'background.primaryGrey1',
                          border: theme =>
                            `1px solid ${theme.palette.border.primaryBlack3}`,
                          '&:hover': {
                            color: 'content.accent1',
                            background: 'background.primaryAccent2',
                            border: theme =>
                              `1px solid ${theme.palette.border.primaryAccent1}`,
                            borderRadius: '4px',
                            cursor: 'pointer',
                          },
                        }}
                        onClick={tagsPopover.onOpen}
                      >
                        <Typography key={tag} variant="caption">
                          {tag}
                        </Typography>
                      </Stack>
                    ))}
                    {tags.length > 1 ? (
                      <Tooltip
                        title={tags
                          .slice(1)
                          .map(
                            tag => tag.charAt(0).toUpperCase() + tag.slice(1),
                          )
                          .join(', ')}
                      >
                        <Stack
                          alignItems="center"
                          justifyContent="center"
                          px="8px"
                          py="3px"
                          sx={{
                            background: 'background.primaryGrey1',
                            border: theme =>
                              `1px solid ${theme.palette.border.primaryBlack3}`,
                            '&:hover': {
                              color: 'content.accent1',
                              background: 'background.primaryAccent2',
                              border: theme =>
                                `1px solid ${theme.palette.border.primaryAccent1}`,
                              borderRadius: '4px',
                              cursor: 'pointer',
                            },
                          }}
                          onClick={tagsPopover.onOpen}
                        >
                          <Typography variant="caption">
                            {`${tags.length - 1}+`}
                          </Typography>
                        </Stack>
                      </Tooltip>
                    ) : null}
                  </Stack>
                </Stack>
                {/* {candidateType === 'JOB_CANDIDATE' && (
                  <Stack direction="row" className={classes.detailsContent}>
                    <Typography variant="input">
                      Candidate Added Date
                    </Typography>
                    <Typography variant="input">
                      {user?.addedDate
                        ? formatFullDateString(user?.addedDate)
                        : '-'}
                    </Typography>
                  </Stack>
                )}
                {candidateType === 'CANDIDATE' && (
                  <Stack direction="row" className={classes.detailsContent}>
                    <Typography variant="input">
                      Candidate Added Date
                    </Typography>
                    <Typography variant="input">
                      {user?.candidateAddedDate
                        ? formatFullDateString(user?.candidateAddedDate)
                        : '-'}
                    </Typography>
                  </Stack>
                )} */}
              </>
            )}
          </Stack>
        </Stack>
      </Stack>
      <Divider />
      <CustomPopover
        open={tagsPopover.open}
        onClose={handleTagsPopover}
        sx={{ width: 200 }}
      >
        <Scrollbar
          sx={{
            height: '180px',
            overflowY: 'auto',
          }}
        >
          {sortedTags &&
            sortedTags
              .filter(tag =>
                tag.toLowerCase().includes(newTagInput.toLowerCase()),
              )
              .map(tag => {
                const isTagFound = tags
                  ? tags.map(t => t.toLowerCase()).includes(tag.toLowerCase())
                  : false;

                return (
                  <MenuItem
                    key={tag}
                    selected={isTagFound}
                    onClick={() => {
                      updateTags(tag);
                    }}
                  >
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                      width="100%"
                    >
                      <Typography variant="caption">{tag}</Typography>
                      {isTagFound && <Iconify icon="tabler:check" width={20} />}
                    </Stack>
                  </MenuItem>
                );
              })}
        </Scrollbar>
        {addNewTag ? (
          <TextField
            value={newTagInput}
            onChange={e => setNewTagInput(e.target.value)}
            placeholder="Enter tag"
            sx={{
              my: '8px',
              '& .MuiOutlinedInput-root': {
                width: 180,
              },
              '& .MuiOutlinedInput-input': {
                minWidth: 0,
              },
            }}
            onKeyDown={event => {
              if (event.key === 'Enter') {
                if (tagExists || !newTagInput) {
                  enqueueSnackbar('Tag already exists!', {
                    variant: 'error',
                  });
                  return;
                }
                handleAddNewTag();
              }
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Stack
                    alignItems="center"
                    justifyContent="center"
                    width="28px"
                    height="28px"
                    bgcolor="background.primaryAccent2"
                    borderRadius="4px"
                    sx={{
                      cursor: 'pointer',
                    }}
                    onClick={() => {
                      if (tagExists || !newTagInput) {
                        enqueueSnackbar('Tag already exists!', {
                          variant: 'error',
                        });
                        return;
                      }
                      handleAddNewTag();
                    }}
                  >
                    <Iconify
                      sx={{
                        color: theme => theme.palette.content.accent1,
                      }}
                      icon="tabler:chevron-right"
                    />
                  </Stack>
                </InputAdornment>
              ),
            }}
          />
        ) : (
          <MenuItem
            onClick={() => {
              setAddNewTag(true);
            }}
          >
            <Iconify icon="tabler:plus" />
            Add New
          </MenuItem>
        )}
      </CustomPopover>
    </>
  );
};

const CandidateTracking = () => {
  const classes = useStyles();
  const { obj: candidateDetails, isLoading } = useContext(
    CandidateProfileContext,
  );

  return (
    <Stack className={classes.component} sx={{ overflowY: 'auto' }}>
      {isLoading ? (
        <Stack spacing="50px">
          <Stack direction="row" alignItems="center" spacing="14px">
            <Skeleton variant="circular" width={14} height={14} />
            <Stack spacing="13px">
              <Skeleton width={128} height={8} />
              <Skeleton
                width={138}
                height={8}
                sx={{
                  backgroundColor: '#A1A1A1',
                }}
              />
            </Stack>
          </Stack>
          <Stack direction="row" alignItems="center" spacing="14px">
            <Skeleton variant="circular" width={14} height={14} />
            <Stack spacing="13px">
              <Skeleton width={128} height={8} />
              <Skeleton
                width={138}
                height={8}
                sx={{
                  backgroundColor: '#A1A1A1',
                }}
              />
            </Stack>
          </Stack>
        </Stack>
      ) : (
        candidateDetails &&
        candidateDetails?.candidate_timeline && (
          <Stack>
            <Typography
              variant="paragraph"
              className={classes.candidateDetailsAndTrackingText}
            >
              Tracking
            </Typography>
            <TrackingTimeline
              data={[...candidateDetails.candidate_timeline].reverse()}
            />
          </Stack>
        )
      )}
    </Stack>
  );
};

const LeftContainer = ({ jobId, setNavGroup, updateCandidateFlag }) => {
  const classes = useStyles();
  const { isLoading } = useCandidateProfileContext();

  return (
    <Stack className={classes.mainDiv}>
      <Stack height="100%" gap="1.2em">
        {!isLoading && (
          <>
            <CandidateSocial
              jobId={jobId}
              setNavGroup={setNavGroup}
              updateCandidateFlag={updateCandidateFlag}
            />
            <CandidateDetails />
            <CandidateTracking />
          </>
        )}
      </Stack>
    </Stack>
  );
};

export default LeftContainer;

LeftContainer.propTypes = {
  jobId: PropTypes.string,
  setNavGroup: PropTypes.func.isRequired,
};

CandidateSocial.propTypes = {
  jobId: PropTypes.string.isRequired,
  setNavGroup: PropTypes.func.isRequired,
};

CandidateDetails.propTypes = {};
